import { useCallback, useRef, useState } from 'react';

import { ActivitiesContentType, LanguageCodes } from 'global/constants';
import { studentAuthApolloContextOption } from 'approot/student-class-to-do/student-class-to-do.utils';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import {
  TextToSpeech,
  TextToSpeechVariables,
} from './__generated__/TextToSpeech';
import useUserData from 'approot/shared/user/user-data';
import { TEXT_TO_SPEECH } from './tts.graphql';
import { useTTSContext } from './tts.context';

type Props = {
  componentId: string;
  version?: string;
  lang: LanguageCodes;
  contentType: ActivitiesContentType;
};

export const useTTSAudio = ({
  componentId,
  version,
  lang,
  contentType,
}: Props) => {
  const [isInPlayMode, setIsInPlayMode] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const { showError } = useSnackbar();

  const {
    isPlaying,
    isPaused,
    currentAudio,
    addAudio,
    playAudio,
    pauseAudio,
    getAudio,
    restartAudio,
    stopAudio,
    tearDown,
  } = useTTSContext({ id: componentId });

  const { isStudentSignedIn } = useUserData();

  const [textToSpeech, { loading }] = useLoggingMutation<
    TextToSpeech,
    TextToSpeechVariables
  >(
    TEXT_TO_SPEECH,
    {
      ...(isStudentSignedIn
        ? { context: studentAuthApolloContextOption() }
        : {}),
      onError: () => {
        setIsInPlayMode(false);
        showError('Failed to generate audio. Please try again later');
      },
    },
    !isStudentSignedIn
  );

  const isAudioPlaying = isPlaying && currentAudio?.itemId === componentId;
  const isAudioPaused = isPaused && currentAudio?.itemId === componentId;
  const loadingAudio = loading;

  const handlePlayAudio = useCallback(async () => {
    setIsInPlayMode(true);

    const audio = getAudio(componentId);
    if (audio) {
      playAudio(componentId);
    } else if (version) {
      const { data } = await textToSpeech({
        variables: {
          input: {
            componentId,
            version,
            contentType,
            language: lang,
          },
        },
      });

      if (data?.textToSpeech) {
        addAudio({
          itemId: componentId,
          audioUrl: data.textToSpeech.url,
          speechMarks: data.textToSpeech.speechMarks,
        });
        playAudio(componentId);
      }
    }
  }, [
    componentId,
    version,
    lang,
    contentType,
    playAudio,
    setIsInPlayMode,
    addAudio,
    getAudio,
    textToSpeech,
  ]);

  return {
    isAudioPlaying,
    isAudioPaused,
    isInPlayMode,
    isPaused,
    containerRef,
    loadingAudio,
    textToSpeech,
    addAudio,
    playAudio,
    pauseAudio,
    getAudio,
    restartAudio,
    stopAudio,
    setIsInPlayMode,
    handlePlayAudio,
    tearDown,
  };
};
